// iter155 — E-Ticaret Premium Header (yeniden tasarım)
// iter156 — Sticky+scroll hide when footer visible, restore on scroll up
import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ShoppingCart, User, LogOut, Search, MapPin, Menu, X, Package,
  Sparkles, ChevronDown, Loader2, Send, Store as StoreIcon, Camera, Phone,
} from "lucide-react";
import { useCart } from "../../context/CartContext";
import { useCustomerAuth } from "../../context/CustomerAuthContext";
import { useCompany } from "../../context/CompanyContext";
import { api } from "../../lib/api";
import useShopTree from "../../hooks/useShopTree";
import { fmtTRY } from "../../lib/format";
import ProductCard from "./ProductCard";

// ─────────────────────── Advanced Search + AI ───────────────────────
// iter163 — `inline` prop: mobil bottom sheet için sonuç panelini absolute yerine akış içinde göster
export function AdvancedSearch({ brandPrimary, inline = false, autoFocus = false }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("search"); // search | ai | visual
  const [data, setData] = useState({ suggestions: [], products: [], brands: [], categories: [] });
  const [loading, setLoading] = useState(false);
  const [visual, setVisual] = useState({ busy: false, keywords: [], products: [], description: "", error: "" });
  const fileRef = useRef(null);
  // iter156.2 — localStorage'tan restore
  const [aiChat, setAiChat] = useState(() => {
    try {
      const raw = localStorage.getItem("bordo:ai:chat:v1");
      if (raw) return JSON.parse(raw);
    } catch {}
    return { session_id: null, messages: [] };
  });
  const [aiBusy, setAiBusy] = useState(false);
  const nav = useNavigate();
  const wrapRef = useRef(null);
  const timerRef = useRef(null);
  const inputRef = useRef(null);

  // iter156.2 — mesaj değişince localStorage'a yaz
  useEffect(() => {
    try {
      // Ürün önerilerini ağır tutmayalım — sadece kısaltılmış hali
      const trimmed = {
        session_id: aiChat.session_id,
        messages: (aiChat.messages || []).slice(-20).map(m => ({
          role: m.role,
          text: m.text,
          products: (m.products || []).slice(0, 3).map(p => ({
            id: p.id, slug: p.slug, sku: p.sku, name: p.name, brand_name: p.brand_name,
            image: p.image || p.primary_image, price: p.price,
          })),
        })),
      };
      localStorage.setItem("bordo:ai:chat:v1", JSON.stringify(trimmed));
    } catch {}
  }, [aiChat]);

  useEffect(() => {
    if (mode !== "search") return;
    if (timerRef.current) clearTimeout(timerRef.current);
    const val = (q || "").trim();
    if (val.length < 2) { setData({ suggestions: [], products: [], brands: [], categories: [] }); return; }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/shop/search/autocomplete?q=${encodeURIComponent(val)}&limit=8`);
        setData(data);
      } catch { /* ignore */ }
      setLoading(false);
    }, 180);
  }, [q, mode]);

  useEffect(() => {
    // iter163 — inline modda dışa tıklama kapatmayı devre dışı bırak (bottom sheet zaten yönetiyor)
    if (inline) { setOpen(true); return; }
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [inline]);

  // iter163 — autoFocus: mobil sheet açıldığında input'a odaklan
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 250);
      return () => clearTimeout(t);
    }
  }, [autoFocus]);

  const doSubmit = (e) => {
    e?.preventDefault?.();
    if (mode === "ai") return sendAI();
    if (!q.trim()) return;
    setOpen(false);
    nav(`/urunler?q=${encodeURIComponent(q)}`);
  };

  const sendAI = async () => {
    const message = q.trim();
    if (!message || aiBusy) return;
    setAiBusy(true);
    setAiChat(c => ({ ...c, messages: [...c.messages, { role: "user", text: message }] }));
    setQ("");
    try {
      const { data: r } = await api.post("/shop/ai-assistant/chat", {
        message,
        session_id: aiChat.session_id,
        history: aiChat.messages.slice(-6),
      });
      setAiChat(c => ({
        session_id: r.session_id,
        messages: [...c.messages, { role: "assistant", text: r.answer, products: r.suggested_products || [] }],
      }));
    } catch (e) {
      setAiChat(c => ({ ...c, messages: [...c.messages, { role: "assistant", text: "Şu an yanıt veremiyorum; lütfen tekrar deneyin veya 0850 244 01 01 arayın." }] }));
    }
    setAiBusy(false);
  };

  const go = (path) => { setOpen(false); setQ(""); nav(path); };
  const total = (data.products?.length || 0) + (data.brands?.length || 0) + (data.categories?.length || 0);

  // iter161 — Görsel arama: kamera / dosya → base64 → /shop/visual-search
  const doVisualSearch = async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      setVisual({ busy: false, keywords: [], products: [], description: "", error: "Görsel en fazla 6MB olmalı" });
      setOpen(true); setMode("visual");
      return;
    }
    setMode("visual"); setOpen(true);
    setVisual({ busy: true, keywords: [], products: [], description: "", error: "" });
    try {
      const b64 = await new Promise((res, rej) => {
        const rd = new FileReader();
        rd.onload = () => res(rd.result);
        rd.onerror = rej;
        rd.readAsDataURL(file);
      });
      const { data: r } = await api.post("/shop/visual-search", { image_base64: b64 });
      setVisual({ busy: false, keywords: r.keywords || [], products: r.products || [], description: r.description || "", error: r.error || "" });
    } catch (e) {
      setVisual({ busy: false, keywords: [], products: [], description: "", error: "AI şu an cevap veremedi, tekrar deneyin" });
    }
  };

  return (
    <div className={inline ? "w-full" : "relative flex-1 max-w-3xl"} ref={wrapRef} data-testid="premium-search">
      <form onSubmit={doSubmit}>
        <div className="relative flex items-center bg-white/95 backdrop-blur border border-zinc-200/80 rounded-full shadow-sm hover:shadow-md transition-shadow focus-within:shadow-lg focus-within:border-primary/40">
          <button type="button" onClick={() => setMode(mode === "ai" ? "search" : "ai")}
                   className={`ml-1.5 px-3 h-9 rounded-full text-xs font-semibold uppercase tracking-wide flex items-center gap-1 transition-all ${mode === "ai" ? "text-white shadow-inner" : "text-zinc-600 hover:text-zinc-900"}`}
                   style={mode === "ai" ? { background: `linear-gradient(135deg, ${brandPrimary}, #4B0F1E)` } : {}}
                   data-testid="search-mode-toggle" title="AI Satış Asistanı">
            <Sparkles size={13}/> {mode === "ai" ? "AI" : "Ara"}
          </button>
          <div className="w-px h-5 bg-zinc-200 mx-1"/>
          <Search size={15} className="text-zinc-400 ml-2"/>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => setOpen(true)}
            placeholder={mode === "ai"
              ? "Bordo AI'ya sor: 'Banyoma ne önerirsin?', 'Beyaz karo var mı?'…"
              : "Ürün, marka, kod veya özellik ara — akıllı öneri yakalanır"}
            className="flex-1 px-3 h-11 bg-transparent text-[14px] focus:outline-none placeholder:text-zinc-400"
            data-testid="premium-search-input"
          />
          {/* iter161 — Görsel arama: kamera ikonu (mobilde direkt kameraya, desktopta dosya seçici) */}
          {mode !== "ai" && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) doVisualSearch(f); e.target.value = ""; }}
                className="hidden"
                data-testid="visual-search-input"
              />
              <button type="button" onClick={() => fileRef.current?.click()}
                       className="mr-1 w-9 h-9 rounded-full flex items-center justify-center hover:bg-zinc-100 text-zinc-500 hover:text-primary transition-colors"
                       title="Fotoğrafla ara" aria-label="Fotoğrafla ara"
                       data-testid="visual-search-btn">
                <Camera size={16}/>
              </button>
            </>
          )}
          {mode === "ai" && (
            <button type="button" onClick={sendAI} disabled={aiBusy || !q.trim()}
                    className="mr-1.5 px-3 h-9 rounded-full text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-40"
                    style={{ background: brandPrimary }} data-testid="ai-send">
              {aiBusy ? <Loader2 size={12} className="animate-spin"/> : <Send size={12}/>} Sor
            </button>
          )}
        </div>
      </form>

      {open && (
        <div className={inline
              ? "mt-3 bg-white border border-zinc-100 rounded-2xl overflow-hidden"
              : "absolute left-0 right-0 top-full mt-2 bg-white border border-zinc-200 rounded-2xl shadow-2xl overflow-hidden z-50 max-h-[75vh] overflow-y-auto"}
             data-testid="premium-search-panel">
          {aiChat.messages.length > 0 && (
            <div className="px-4 pt-2 flex items-center justify-between border-b border-zinc-100 pb-2 -mt-2">
              <div className="text-[10px] text-zinc-400">{aiChat.messages.length} mesaj · Devam eden sohbet</div>
              <button onClick={() => { setAiChat({ session_id: null, messages: [] }); try { localStorage.removeItem("bordo:ai:chat:v1"); } catch {} }}
                       className="text-[10px] text-zinc-500 hover:text-red-600 font-semibold uppercase tracking-widest inline-flex items-center gap-1"
                       data-testid="ai-clear-chat">
                <X size={10}/> Temizle
              </button>
            </div>
          )}
          {mode === "ai" ? (
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2 flex items-center gap-1">
                <Sparkles size={11}/> Bordo AI Satış Temsilcisi
              </div>
              {aiChat.messages.length === 0 && (
                <div className="p-4 bg-gradient-to-br from-rose-50 to-amber-50 rounded-xl text-sm text-zinc-700">
                  Merhaba! Size hangi ürünü, hangi bütçeyi ve nereye uygulayacağınızı söyleyin —
                  size en uygun seçenekleri anında listeleyeyim.
                </div>
              )}
              <div className={inline ? "space-y-2" : "space-y-2 max-h-[45vh] overflow-y-auto"}>
                {aiChat.messages.map((m, i) => (
                  <div key={i}>
                    <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${m.role === "user"
                        ? "bg-primary text-white rounded-br-sm"
                        : "bg-zinc-100 text-zinc-800 rounded-bl-sm"}`}
                        data-testid={`ai-msg-${m.role}`}>
                        <div className="whitespace-pre-wrap">{m.text}</div>
                      </div>
                    </div>
                    {/* iter156.1 — AI ürün önerileri */}
                    {m.role === "assistant" && m.products?.length > 0 && (
                      <div className="mt-2">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5 pl-1">
                          <Sparkles size={9} className="inline mr-1"/> Sizin için öneriler
                        </div>
                        <div className="grid grid-cols-3 gap-2" data-testid="ai-suggested-products">
                          {m.products.slice(0, 3).map(p => (
                            <ProductCard key={p.id} p={p}/>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : mode === "visual" ? (
            <div className="p-4" data-testid="visual-search-panel">
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2 flex items-center gap-1">
                <Camera size={11}/> Görsel Arama — Bordo AI benzer ürünleri buluyor
              </div>
              {visual.busy && (
                <div className="p-6 text-center bg-gradient-to-br from-rose-50 to-amber-50 rounded-xl">
                  <Loader2 size={22} className="animate-spin mx-auto text-primary mb-2"/>
                  <div className="text-sm text-zinc-700">Fotoğrafı analiz ediyorum…</div>
                </div>
              )}
              {!visual.busy && visual.error && (
                <div className="p-4 bg-red-50 rounded-xl text-sm text-red-700" data-testid="visual-error">{visual.error}</div>
              )}
              {!visual.busy && visual.description && (
                <div className="mb-3 p-3 bg-zinc-50 rounded-xl text-[12px] text-zinc-700 leading-snug">
                  <strong className="text-zinc-900">AI:</strong> {visual.description}
                </div>
              )}
              {!visual.busy && visual.keywords?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3" data-testid="visual-keywords">
                  {visual.keywords.slice(0, 8).map((k, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-semibold">{k}</span>
                  ))}
                </div>
              )}
              {!visual.busy && visual.products?.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="visual-results">
                  {visual.products.slice(0, 9).map(p => <ProductCard key={p.id} p={p}/>)}
                </div>
              )}
              {!visual.busy && !visual.error && visual.products?.length === 0 && visual.keywords?.length === 0 && (
                <div className="p-6 text-center text-sm text-zinc-500">
                  <Camera size={22} className="mx-auto text-zinc-300 mb-2"/>
                  Kamera ikonuna tıklayıp bir fotoğraf yükleyin — benzer ürünleri gösterelim.
                </div>
              )}
              <button onClick={() => fileRef.current?.click()}
                       className="mt-3 w-full py-2.5 rounded-full text-white text-sm font-semibold flex items-center justify-center gap-2"
                       style={{ background: brandPrimary }} data-testid="visual-retry-btn">
                <Camera size={14}/> Başka Fotoğraf Yükle
              </button>
            </div>
          ) : (
            <>
              {loading && total === 0 && <div className="p-4 text-center text-xs text-zinc-500"><Loader2 size={14} className="inline animate-spin mr-1"/>Aranıyor…</div>}
              {!loading && total === 0 && q.length >= 2 && (
                <div className="p-6 text-center text-sm text-zinc-500">
                  <Search size={22} className="mx-auto text-zinc-300 mb-2"/>
                  "<b>{q}</b>" için sonuç bulunamadı — <button onClick={() => setMode("ai")} className="text-primary underline font-semibold">AI'ya sorun</button>
                </div>
              )}
              {data.categories?.length > 0 && (
                <div className="p-2">
                  <div className="px-2 py-1 text-[10px] uppercase tracking-widest font-bold text-zinc-500">Kategoriler</div>
                  {data.categories.map(c => (
                    <button key={c.id} onClick={() => go(`/kategori/${c.slug || c.id}`)}
                             className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm hover:bg-zinc-50 text-left" data-testid={`sug-cat-${c.id}`}>
                      <span className="w-8 h-8 rounded bg-primary/10 text-primary flex items-center justify-center"><Package size={13}/></span>
                      <span className="text-zinc-800 font-medium">{c.name}</span>
                    </button>
                  ))}
                </div>
              )}
              {data.brands?.length > 0 && (
                <div className="p-2 border-t border-zinc-100">
                  <div className="px-2 py-1 text-[10px] uppercase tracking-widest font-bold text-zinc-500">Markalar</div>
                  {data.brands.map(b => (
                    <button key={b.id} onClick={() => go(`/urunler?marka=${b.id}`)}
                             className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm hover:bg-zinc-50 text-left" data-testid={`sug-brand-${b.id}`}>
                      {b.logo ? <img src={b.logo} alt={b.name} className="w-8 h-8 object-contain"/> : <span className="w-8 h-8 rounded bg-zinc-100"/>}
                      <span className="text-zinc-800 font-medium">{b.name}</span>
                    </button>
                  ))}
                </div>
              )}
              {data.products?.length > 0 && (
                <div className="p-2 border-t border-zinc-100">
                  <div className="px-2 py-1 text-[10px] uppercase tracking-widest font-bold text-zinc-500">Ürünler</div>
                  {data.products.map(p => (
                    <button key={p.id} onClick={() => go(`/urun/${p.slug || p.sku || p.id}`)}
                             className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-zinc-50 text-left" data-testid={`sug-product-${p.id}`}>
                      <div className="w-12 h-12 rounded-lg bg-zinc-100 overflow-hidden shrink-0">
                        {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover"/> : <Package size={18} className="text-zinc-300 m-auto"/>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-zinc-900 truncate">{p.name}{p.fuzzy && <span className="ml-1.5 text-[9px] text-amber-600 font-semibold">YAKIN EŞLEŞME</span>}</div>
                        <div className="text-[11px] text-zinc-500 truncate">{p.brand_name || "—"} · <span className="font-mono">{p.sku}</span></div>
                      </div>
                      {p.price > 0 && <div className="text-sm font-bold tabular-nums shrink-0 text-zinc-700">{fmtTRY(p.price)}</div>}
                    </button>
                  ))}
                </div>
              )}
              {total > 0 && (
                <button type="button" onClick={() => go(`/urunler?q=${encodeURIComponent(q)}`)}
                         className="w-full py-3 text-center text-xs font-semibold border-t border-zinc-100 hover:bg-zinc-50 text-primary" data-testid="see-all-results">
                  Tüm sonuçları göster →
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────── Store Locator Popover ───────────────────────
export function StoreLocatorBtn({ brandPrimary }) {
  const [open, setOpen] = useState(false);
  const [stores, setStores] = useState([]);
  const ref = useRef(null);
  useEffect(() => {
    api.get("/seo/stores").then(r => setStores(Array.isArray(r.data) ? r.data : (r.data?.stores || []))).catch(() => {});
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const dirs = (s) => s.maps?.google_directions || (s.lat && s.lng
      ? `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((s.name || "") + " " + (s.address || ""))}`);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(v => !v)}
               className="flex items-center gap-1.5 px-3 h-10 rounded-full border border-zinc-200 hover:border-primary/40 hover:bg-primary/5 text-sm font-medium text-zinc-700 transition-all"
               data-testid="header-store-locator" aria-label="Mağazalarımız">
        <MapPin size={14} className="text-primary"/>
        <span className="hidden md:inline">Mağazalar</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-[340px] bg-white border border-zinc-200 rounded-2xl shadow-2xl overflow-hidden z-50" data-testid="store-locator-panel">
          <div className="px-4 py-3 border-b border-zinc-100 bg-gradient-to-r from-zinc-900 to-zinc-800 text-white">
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">Fiziksel Mağazalar</div>
            <div className="text-sm font-semibold">Bize ziyarete gelin</div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-zinc-100">
            {stores.length === 0 && <div className="p-6 text-center text-xs text-zinc-500">Mağaza bilgisi yükleniyor…</div>}
            {stores.slice(0, 6).map(s => (
              <div key={s.id} className="p-3 hover:bg-zinc-50" data-testid={`store-item-${s.id}`}>
                <div className="flex items-start gap-2">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <StoreIcon size={14}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-zinc-900">{s.name || s.district || "Mağaza"}</div>
                    <div className="text-[11px] text-zinc-600 leading-snug mt-0.5">{s.address}</div>
                    {s.phone && <a href={`tel:${s.phone.replace(/[^0-9+]/g, "")}`} className="text-[11px] text-primary font-semibold hover:underline mt-1 inline-block">{s.phone}</a>}
                  </div>
                </div>
                <div className="mt-2 flex gap-1.5">
                  <a href={dirs(s)} target="_blank" rel="noreferrer"
                     className="flex-1 text-center px-3 py-1.5 rounded-full text-white text-[11px] font-semibold hover:opacity-90"
                     style={{ background: brandPrimary }} data-testid={`store-directions-${s.id}`}>
                    Yol Tarifi
                  </a>
                  <Link to={`/magaza/${s.id}`} onClick={() => setOpen(false)}
                        className="flex-1 text-center px-3 py-1.5 rounded-full border border-zinc-200 text-[11px] font-semibold text-zinc-700 hover:border-primary/40">
                    Detay
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <Link to="/magazalar" onClick={() => setOpen(false)}
                className="block w-full text-center py-2.5 text-[11px] font-semibold border-t border-zinc-100 text-primary hover:bg-zinc-50">
            Tüm Mağazaları Gör →
          </Link>
        </div>
      )}
    </div>
  );
}

// ─────────────────────── Apple-style Category Bar ───────────────────────
function AppleCategoryBar({ brandPrimary }) {
  const { tree } = useShopTree();
  const [active, setActive] = useState(null);
  const closeT = useRef(null);
  const cats = (tree.categories || []).filter(c => (c.product_count || 0) > 0 || (c.children || []).length > 0);
  const openC = (id) => { if (closeT.current) clearTimeout(closeT.current); setActive(id); };
  const scheduleClose = () => { if (closeT.current) clearTimeout(closeT.current); closeT.current = setTimeout(() => setActive(null), 180); };
  const activeCat = cats.find(c => c.id === active);
  return (
    <div className="hidden lg:block relative border-t border-zinc-100/60 bg-white/80 backdrop-blur-xl" onMouseLeave={scheduleClose} data-testid="apple-catbar">
      <div className="max-w-[1400px] mx-auto px-6 flex items-center justify-center gap-1 h-11">
        {cats.map(c => {
          const isA = c.id === active;
          return (
            <button
              key={c.id}
              onMouseEnter={() => openC(c.id)}
              onFocus={() => openC(c.id)}
              onClick={() => window.location.assign(`/kategori/${c.slug || c.id}`)}
              data-testid={`apple-cat-${c.slug || c.id}`}
              className={`px-4 h-11 text-[13px] font-medium tracking-tight transition-colors ${isA ? "text-zinc-900" : "text-zinc-600 hover:text-zinc-900"}`}
            >
              {c.name}
            </button>
          );
        })}
      </div>
      {activeCat && (
        <div
          onMouseEnter={() => openC(activeCat.id)}
          onMouseLeave={scheduleClose}
          className="absolute left-0 right-0 top-full bg-white border-t border-zinc-100 shadow-2xl z-40 animate-in fade-in slide-in-from-top-1 duration-150"
          data-testid={`apple-panel-${activeCat.slug || activeCat.id}`}
        >
          <div className="max-w-[1400px] mx-auto px-8 py-10 grid grid-cols-12 gap-10">
            <div className="col-span-7">
              <div className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold mb-2">Keşfet</div>
              <Link to={`/kategori/${activeCat.slug || activeCat.id}`}
                    className="font-display font-semibold text-2xl text-zinc-900 hover:text-primary transition-colors">
                Tüm {activeCat.name} →
              </Link>
              {(activeCat.children || []).length > 0 && (
                <div className="mt-6 grid grid-cols-3 gap-x-6 gap-y-2">
                  {activeCat.children.map(sc => (
                    <Link key={sc.id} to={`/kategori/${sc.slug || sc.id}`}
                          data-testid={`apple-sub-${sc.slug || sc.id}`}
                          className="text-[13px] text-zinc-700 hover:text-primary py-1.5 font-normal">
                      {sc.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <div className="col-span-5 border-l border-zinc-100 pl-10">
              <div className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold mb-3">Öne Çıkanlar</div>
              {(activeCat.preview || []).length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {activeCat.preview.slice(0, 4).map(p => (
                    <Link key={p.id} to={`/urun/${p.sku || p.id}`}
                          data-testid={`apple-preview-${p.id}`}
                          className="group">
                      <div className="aspect-square rounded-2xl bg-zinc-50 overflow-hidden mb-2">
                        {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/> : <div className="w-full h-full flex items-center justify-center"><Package size={20} className="text-zinc-300"/></div>}
                      </div>
                      <div className="text-[12px] font-medium text-zinc-800 line-clamp-2 group-hover:text-primary">{p.name}</div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-zinc-400 italic">Yakında yeni ürünler eklenecek</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────── PublicHeader ───────────────────────
export default function PublicHeader() {
  const { count } = useCart();
  const { customer, logout } = useCustomerAuth();
  const { company } = useCompany();
  const [scrolled, setScrolled] = useState(false);

  const brandName = company?.company_name || "Bordo Seramik";
  const brandPrimary = company?.brand_primary || "#8D1B3D";
  const logoSrc = company?.logo_url || "";
  const initial = (brandName.trim()[0] || "B").toUpperCase();

  // Scroll-tabanlı efekt — sadece gölge/opasite
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 bg-white/95 backdrop-blur-xl transition-shadow duration-300 ${scrolled ? "shadow-md border-b border-zinc-100" : "border-b border-transparent"}`}
      data-testid="premium-header"
    >
      {/* iter162 — Mobilde sadece sade logo + iletişim numarası; desktop tam özellikli */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-14 md:h-16 flex items-center gap-3 sm:gap-5">
        <Link to="/" className="flex items-center gap-2 shrink-0 md:mx-0" data-testid="site-logo">
          {logoSrc ? (
            <img src={logoSrc} alt={brandName} className="h-9 max-w-[160px] object-contain"/>
          ) : (
            <>
              <div className="w-9 h-9 rounded-lg text-white flex items-center justify-center font-bold text-lg font-display shadow-sm" style={{ background: brandPrimary }}>{initial}</div>
              <div>
                <div className="font-display font-bold text-base text-zinc-900 leading-tight">{brandName}</div>
                <div className="text-[9px] text-zinc-500 uppercase tracking-widest hidden sm:block">Premium Seramik</div>
              </div>
            </>
          )}
        </Link>

        {/* Mobil: iletişim numarası — sadece md altında */}
        {company?.phone && (
          <a href={`tel:${company.phone.replace(/[^0-9+]/g, "")}`}
             className="md:hidden ml-auto flex items-center gap-1.5 px-3 h-9 rounded-full text-[12px] font-semibold text-white shadow-sm active:scale-95 transition-transform"
             style={{ background: brandPrimary }}
             data-testid="mobile-header-phone">
            <Phone size={13}/>
            <span className="tabular-nums">{company.phone.replace(/^\+?90/, "0").replace(/[^0-9]/g, "").replace(/^(\d{4})(\d{3})(\d{2})(\d{2}).*/, "$1 $2 $3 $4")}</span>
          </a>
        )}

        {/* Desktop-only: arama + mağazalar + auth + sepet */}
        <div className="hidden md:flex flex-1">
          <AdvancedSearch brandPrimary={brandPrimary}/>
        </div>

        <div className="hidden md:flex items-center gap-2 shrink-0">
          <StoreLocatorBtn brandPrimary={brandPrimary}/>
          {customer ? (
            <>
              <Link to="/hesap" className="flex items-center gap-1.5 px-3 h-10 rounded-full text-sm font-medium text-zinc-700 hover:text-zinc-900 hover:bg-zinc-50" data-testid="site-account">
                <User size={15}/> <span className="max-w-[100px] truncate">{customer.customer?.name?.split(" ")[0] || "Hesabım"}</span>
              </Link>
              <button onClick={logout} title="Çıkış Yap" className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-red-50 text-zinc-500 hover:text-red-600" data-testid="site-logout">
                <LogOut size={15}/>
              </button>
            </>
          ) : (
            <Link to="/hesap/giris" className="flex items-center gap-1.5 px-3 h-10 rounded-full text-sm font-medium text-zinc-700 hover:text-zinc-900 hover:bg-zinc-50" data-testid="site-signin">
              <User size={15}/> Giriş / Kayıt
            </Link>
          )}
          <Link to="/sepet" className="relative flex items-center gap-1.5 px-3 h-10 rounded-full text-white text-sm font-semibold hover:opacity-90 shadow-sm"
                style={{ background: brandPrimary }} data-testid="site-cart">
            <ShoppingCart size={15}/> <span className="hidden sm:inline">Sepet</span>
            {count > 0 && <span className="ml-0.5 min-w-[20px] h-5 px-1.5 bg-white/25 rounded-full text-[10px] font-bold flex items-center justify-center">{count}</span>}
          </Link>
        </div>
      </div>

      <AppleCategoryBar brandPrimary={brandPrimary}/>
    </header>
  );
}
